import {
  countMonthlyItemIcons,
  hasValidRiskLevel
} from './monthlyItemIconUtils';
import { formatMonthlyDateBadgeLabel } from './formatMonthlyDateBadgeLabel';
import { formatMonthlyItemDisplayTitle } from './formatMonthlyItemDisplayTitle';
import {
  getMonthlyExpandedBlockWidthPx,
  getMonthlyRegularChildren,
  isMonthlyItemExpandable
} from './monthlyExpandUtils';
import { MONTHLY_TREE_CHILD_INDENT_PX } from './monthlyLayoutConstants';
import { getReleasePhaseIcon } from 'utils/helpers';
import type { MonthlyTimelineItem } from './types';

const BADGE_HORIZONTAL_PADDING_PX = 8;
const BADGE_BORDER_PX = 2;
const BADGE_TITLE_GAP_PX = 6;
/** Buffer for sub-pixel text measurement and Spectrum button chrome. */
const WIDTH_ESTIMATE_BUFFER_PX = 12;
const TREE_CHEVRON_WIDTH_PX = 22;
const ICON_SLOT_WIDTH_PX = 16;
const ICON_GAP_PX = 4;
const ICONS_TITLE_GAP_PX = 2;
const FEATURE_CONTENT_GAP_PX = 6;
const FEATURE_BADGE_HORIZONTAL_PADDING_PX = 8;
const FALLBACK_CHAR_WIDTH_PX = 7;
const TEXT_FONT_FAMILY =
  "Adobe Clean, adobe-clean, 'Source Sans Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

let measureCanvas: HTMLCanvasElement | null = null;

function measureText(text: string, fontWeight: number): number {
  if (!text) return 0;

  if (typeof document === 'undefined') {
    return text.length * FALLBACK_CHAR_WIDTH_PX;
  }

  if (!measureCanvas) {
    measureCanvas = document.createElement('canvas');
  }

  const context = measureCanvas.getContext('2d');
  if (!context) {
    return text.length * FALLBACK_CHAR_WIDTH_PX;
  }

  context.font = `${fontWeight} 12px ${TEXT_FONT_FAMILY}`;
  return context.measureText(text).width;
}

export function estimateMonthlyInlineItemWidth(
  title: string,
  badgeLabel: string,
  laneLabel: string,
  hasChevron = false,
  iconCount = 0
): number {
  const isEventLane = laneLabel.toLowerCase().includes('event');
  const titleWeight = isEventLane ? 500 : 700;

  const badgeWidth =
    measureText(badgeLabel.toUpperCase(), 700) +
    BADGE_HORIZONTAL_PADDING_PX +
    BADGE_BORDER_PX;
  const titleWidth = measureText(title || 'Untitled', titleWeight);

  let total = badgeWidth + BADGE_TITLE_GAP_PX + titleWidth;

  if (iconCount > 0) {
    total +=
      ICONS_TITLE_GAP_PX +
      iconCount * ICON_SLOT_WIDTH_PX +
      Math.max(0, iconCount - 1) * ICON_GAP_PX;
  }

  if (hasChevron) {
    total += TREE_CHEVRON_WIDTH_PX;
  }

  return Math.ceil(total) + WIDTH_ESTIMATE_BUFFER_PX;
}

/** Width of a monthly inline feature row (title + trailing badges/icons). */
export function estimateMonthlyFeatureInlineWidth(
  feature: MonthlyTimelineItem
): number {
  const titleWidth = measureText(feature.title || 'Untitled', 500);
  let trailingWidth = 0;

  if (feature.mvp) {
    trailingWidth +=
      measureText('MVP', 500) + FEATURE_BADGE_HORIZONTAL_PADDING_PX;
  }

  const phaseIcon = feature.releasePhase
    ? getReleasePhaseIcon(feature.releasePhase)
    : null;
  if (phaseIcon) {
    trailingWidth +=
      (trailingWidth > 0 ? FEATURE_CONTENT_GAP_PX : 0) + ICON_SLOT_WIDTH_PX;
  }

  const products = feature.products;
  const hasProducts = Array.isArray(products)
    ? products.length > 0
    : Boolean(products);
  if (hasProducts) {
    trailingWidth +=
      (trailingWidth > 0 ? FEATURE_CONTENT_GAP_PX : 0) + ICON_SLOT_WIDTH_PX;
  }

  if (hasValidRiskLevel(feature.riskLevel)) {
    trailingWidth +=
      (trailingWidth > 0 ? FEATURE_CONTENT_GAP_PX : 0) + ICON_SLOT_WIDTH_PX;
  }

  let total = titleWidth;
  if (trailingWidth > 0) {
    total += FEATURE_CONTENT_GAP_PX + trailingWidth;
  }

  return Math.ceil(total) + WIDTH_ESTIMATE_BUFFER_PX;
}

/**
 * Placement width: collapsed by default; when expanded, only grow horizontally
 * if child/feature content is wider than the parent card.
 */
export function estimateMonthlyPlacementBlockWidth(
  item: MonthlyTimelineItem,
  laneLabel: string,
  isExpanded: boolean
): number {
  const collapsedWidth = estimateMonthlyItemBlockWidth(item, laneLabel, false);

  if (!isExpanded) {
    return collapsedWidth;
  }

  const expandedWidth = estimateMonthlyItemBlockWidth(item, laneLabel, true);
  return expandedWidth > collapsedWidth ? expandedWidth : collapsedWidth;
}

export function estimateMonthlyItemBlockWidth(
  item: MonthlyTimelineItem,
  laneLabel: string,
  isExpanded = false
): number {
  const hasChevron = isMonthlyItemExpandable(item);
  const badgeLabel = formatMonthlyDateBadgeLabel(item, laneLabel);

  const rootIconCount = countMonthlyItemIcons(item, laneLabel);
  const rootWidth = estimateMonthlyInlineItemWidth(
    formatMonthlyItemDisplayTitle(item),
    badgeLabel,
    laneLabel,
    hasChevron,
    rootIconCount
  );

  if (!isExpanded) {
    return rootWidth;
  }

  const estimateChildWidth = (child: MonthlyTimelineItem) =>
    estimateMonthlyInlineItemWidth(
      formatMonthlyItemDisplayTitle(child),
      formatMonthlyDateBadgeLabel(child, laneLabel),
      laneLabel,
      false,
      countMonthlyItemIcons(child, laneLabel)
    ) + MONTHLY_TREE_CHILD_INDENT_PX;

  const estimateFeatureWidth = (feature: MonthlyTimelineItem) =>
    estimateMonthlyFeatureInlineWidth(feature);

  return getMonthlyExpandedBlockWidthPx(
    item,
    laneLabel,
    rootWidth,
    estimateChildWidth,
    estimateFeatureWidth
  );
}
